import React, { useState } from 'react';
import { Search, Edit2, Trash2, Users, Building, CreditCard, Settings, Eye, UserPlus, Crown, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from './Button';
import { AiOutlineAntDesign } from 'react-icons/ai';
import Template from '../screen/Template';
import BulkHsnUpload from './invoice/BulkHsnUpload';

const AdminPanel = () => {
    const [activeTab, setActiveTab] = useState('overview');

    const [users] = useState([
        {
            id: '1',
            name: 'John Doe',
            email: 'john@company.com',
            role: 'Admin',
            status: 'Active',
            lastLogin: '2024-01-15 10:30 AM',
            assignedClients: 15,
            permissions: ['all'],
            createdDate: '2023-06-01'
        },
        {
            id: '2',
            name: 'Jane Smith',
            email: 'jane@company.com',
            role: 'Manager',
            status: 'Active',
            lastLogin: '2024-01-15 09:15 AM',
            assignedClients: 25,
            permissions: ['view_clients', 'edit_clients', 'view_reports'],
            createdDate: '2023-07-15'
        },
        {
            id: '3',
            name: 'Mike Johnson',
            email: 'mike@company.com',
            role: 'Accountant',
            status: 'Active',
            lastLogin: '2024-01-14 04:45 PM',
            assignedClients: 12,
            permissions: ['view_clients', 'edit_gst', 'file_returns'],
            createdDate: '2023-08-20'
        }
    ]);

    const [roles] = useState([
        {
            id: '1',
            name: 'Admin',
            description: 'Full system access with all permissions',
            permissions: ['all'],
            userCount: 1
        },
        {
            id: '2',
            name: 'Manager',
            description: 'Manage teams and oversee operations',
            permissions: ['view_clients', 'edit_clients', 'view_reports', 'manage_users'],
            userCount: 1
        },
        {
            id: '3',
            name: 'Accountant',
            description: 'Handle client accounts and tax filings',
            permissions: ['view_clients', 'edit_gst', 'file_returns', 'generate_reports'],
            userCount: 1
        },
        {
            id: '4',
            name: 'Viewer',
            description: 'Read-only access to assigned clients',
            permissions: ['view_clients', 'view_reports'],
            userCount: 0
        }
    ]);

    const [subscription] = useState({
        planName: 'Pro Variant',
        userLimit: 25,
        clientLimit: 100,
        currentUsers: 3,
        currentClients: 45,
        billingCycle: 'Monthly',
        nextBilling: '2024-02-15',
        amount: 4999,
        features: ['Advanced Reporting', 'API Access', 'Priority Support', 'Custom Workflows'],
        status: 'Active'
    });

    const subscriptionPlans = [
        {
            name: 'Base Variant',
            userLimit: 10,
            clientLimit: 40,
            price: 1999,
            features: ['Basic GST Filing', 'Document Storage', 'Email Support']
        },
        {
            name: 'Pro Variant',
            userLimit: 25,
            clientLimit: 100,
            price: 4999,
            features: ['Advanced Reporting', 'API Access', 'Priority Support', 'Custom Workflows']
        },
        {
            name: 'Enterprise Variant',
            userLimit: -1,
            clientLimit: -1,
            price: 9999,
            features: ['Unlimited Everything', 'Dedicated Support', 'Custom Integrations', 'White Label']
        }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return 'bg-green-100 text-green-800';
            case 'Inactive': return 'bg-red-100 text-red-800';
            case 'Pending': return 'bg-yellow-100 text-yellow-800';
            case 'Expired': return 'bg-red-100 text-red-800';
            case 'Cancelled': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'Admin': return 'bg-purple-100 text-purple-800';
            case 'Manager': return 'bg-blue-100 text-blue-800';
            case 'Accountant': return 'bg-green-100 text-green-800';
            case 'Viewer': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const tabs = [
        { id: 'subscription', label: 'Subscription', icon: CreditCard },
        { id: 'system', label: 'System Settings', icon: Settings },
        { id: 'template', label: 'Template', icon: AiOutlineAntDesign },
        { id: 'hsn', label: 'HSN', icon: AiOutlineAntDesign }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
                    <div className="flex space-x-3">
                        <Button label="Bulk Invite" />

                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <tab.icon className="h-4 w-4" />
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="space-y-6">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                                    <p className="text-2xl font-bold text-blue-600">{subscription.currentUsers}</p>
                                    <p className="text-sm text-gray-500">of {subscription.userLimit} limit</p>
                                </div>
                                <Users className="h-8 w-8 text-blue-500" />
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Client Accounts</p>
                                    <p className="text-2xl font-bold text-green-600">{subscription.currentClients}</p>
                                    <p className="text-sm text-gray-500">of {subscription.clientLimit} limit</p>
                                </div>
                                <Building className="h-8 w-8 text-green-500" />
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Active Roles</p>
                                    <p className="text-2xl font-bold text-purple-600">{roles.length}</p>
                                    <p className="text-sm text-gray-500">Custom roles</p>
                                </div>
                                <Crown className="h-8 w-8 text-purple-500" />
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Subscription</p>
                                    <p className="text-2xl font-bold text-orange-600">{subscription.planName}</p>
                                    <p className="text-sm text-gray-500">₹{subscription.amount}/month</p>
                                </div>
                                <CreditCard className="h-8 w-8 text-orange-500" />
                            </div>
                        </div>
                    </div>

                    {/* System Health */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health & Alerts</h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                <div className="flex items-center space-x-3">
                                    <CheckCircle className="h-5 w-5 text-green-500" />
                                    <span className="text-green-800">All systems operational</span>
                                </div>
                                <span className="text-green-600 text-sm">Last checked: 2 min ago</span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                                <div className="flex items-center space-x-3">
                                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                                    <span className="text-yellow-800">Approaching user limit (3/{subscription.userLimit})</span>
                                </div>
                                <button className="text-yellow-600 text-sm hover:underline">Upgrade Plan</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* User Management Tab */}
            {activeTab === 'users' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
                                <div className="flex space-x-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Search users..."
                                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                    <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                                        <option value="">All Roles</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.name}>{role.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            User
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Role
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Assigned Clients
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Login
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {users.map((user) => (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center">
                                                        <span className="text-white font-medium text-sm">
                                                            {user.name.split(' ').map(n => n[0]).join('')}
                                                        </span>
                                                    </div>
                                                    <div className="ml-3">
                                                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                        <div className="text-sm text-gray-500">{user.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.status)}`}>
                                                    {user.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {user.assignedClients}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {user.lastLogin}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    <button className="text-blue-600 hover:text-blue-900">
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                    <button className="text-gray-600 hover:text-gray-900">
                                                        <Edit2 className="h-4 w-4" />
                                                    </button>
                                                    <button className="text-red-600 hover:text-red-900">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Role Management Tab */}
            {activeTab === 'roles' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {roles.map((role) => (
                            <div key={role.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">{role.name}</h3>
                                        <p className="text-sm text-gray-500">{role.description}</p>
                                    </div>
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                        {role.userCount} users
                                    </span>
                                </div>

                                <div className="space-y-3">
                                    <h4 className="font-medium text-gray-900">Permissions</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {role.permissions.includes('all') ? (
                                            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                                                All Permissions
                                            </span>
                                        ) : (
                                            role.permissions.map((permission) => (
                                                <span key={permission} className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                                                    {permission.replace('_', ' ')}
                                                </span>
                                            ))
                                        )}
                                    </div>
                                </div>

                                <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-200">
                                    <button className="text-blue-600 hover:text-blue-800 text-sm">Edit Role</button>
                                    <button className="text-red-600 hover:text-red-800 text-sm">Delete Role</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
                <div className="space-y-6">
                    {/* Current Subscription */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Subscription</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Plan Name</label>
                                    <p className="text-lg font-semibold text-gray-900">{subscription.planName}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Billing Cycle</label>
                                    <p className="text-sm text-gray-900">{subscription.billingCycle}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Next Billing Date</label>
                                    <p className="text-sm text-gray-900">{subscription.nextBilling}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Amount</label>
                                    <p className="text-lg font-semibold text-green-600">₹{subscription.amount}/month</p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Usage</label>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span>Users: {subscription.currentUsers}/{subscription.userLimit}</span>
                                            <span>{Math.round((subscription.currentUsers / subscription.userLimit) * 100)}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-blue-600 h-2 rounded-full"
                                                style={{ width: `${(subscription.currentUsers / subscription.userLimit) * 100}%` }}
                                            ></div>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span>Clients: {subscription.currentClients}/{subscription.clientLimit}</span>
                                            <span>{Math.round((subscription.currentClients / subscription.clientLimit) * 100)}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-green-600 h-2 rounded-full"
                                                style={{ width: `${(subscription.currentClients / subscription.clientLimit) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Features</label>
                                    <div className="space-y-1">
                                        {subscription.features.map((feature, index) => (
                                            <div key={index} className="flex items-center space-x-2">
                                                <CheckCircle className="h-4 w-4 text-green-500" />
                                                <span className="text-sm text-gray-700">{feature}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Available Plans */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Plans</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {subscriptionPlans.map((plan, index) => (
                                <div key={index} className={`border-2 rounded-lg p-6 ${plan.name === subscription.planName
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}>
                                    <div className="text-center">
                                        <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                                        <div className="mt-2">
                                            <span className="text-3xl font-bold text-gray-900">₹{plan.price}</span>
                                            <span className="text-gray-500">/month</span>
                                        </div>
                                    </div>

                                    <div className="mt-4 space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span>Users:</span>
                                            <span>{plan.userLimit === -1 ? 'Unlimited' : plan.userLimit}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span>Clients:</span>
                                            <span>{plan.clientLimit === -1 ? 'Unlimited' : plan.clientLimit}</span>
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <h5 className="font-medium text-gray-900 mb-2">Features:</h5>
                                        <ul className="space-y-1">
                                            {plan.features.map((feature, featureIndex) => (
                                                <li key={featureIndex} className="flex items-center space-x-2">
                                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                                    <span className="text-sm text-gray-700">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="mt-6">
                                        {plan.name === subscription.planName ? (
                                            <button className="w-full bg-blue-100 text-blue-700 py-2 px-4 rounded-lg font-medium">
                                                Current Plan
                                            </button>
                                        ) : (
                                            <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium">
                                                {plan.price > subscription.amount ? 'Upgrade' : 'Downgrade'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* System Settings Tab */}
            {activeTab === 'system' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Configuration</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-900">Security Settings</h4>
                                <div className="space-y-3">
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">Two-factor authentication required</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">End-to-end encryption</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">Automatic session timeout</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                        <span className="ml-2 text-sm text-gray-700">IP whitelist enforcement</span>
                                    </label>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-900">Integration Settings</h4>
                                <div className="space-y-3">
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">GST Portal API integration</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                        <span className="ml-2 text-sm text-gray-700">Bank reconciliation API</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">Email notifications</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">SMS notifications</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Management</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-900">Backup Settings</h4>
                                <div className="space-y-3">
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">Daily automatic backups</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">Cloud backup storage</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                        <span className="ml-2 text-sm text-gray-700">Real-time data replication</span>
                                    </label>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <h4 className="font-medium text-gray-900">Data Retention</h4>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Document retention period</label>
                                        <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                                            <option value="1">1 year</option>
                                            <option value="3">3 years</option>
                                            <option value="5" selected>5 years</option>
                                            <option value="7">7 years</option>
                                            <option value="-1">Indefinite</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Log retention period</label>
                                        <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                                            <option value="30">30 days</option>
                                            <option value="90" selected>90 days</option>
                                            <option value="180">180 days</option>
                                            <option value="365">1 year</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'template' && (
                <Template />
            )}
            {activeTab === 'hsn' && (
                <BulkHsnUpload />
            )}
        </div>
    );
};

export default AdminPanel;